import { FormEvent, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '../AdminLayout';
import {
  createPortfolioItem,
  fetchAdminPortfolioItem,
  updatePortfolioItem,
} from '../adminService';
import { useAdminSession } from '../adminSession';
import { usePortfolioCategories } from '../../Portfolio/portfolioService';
import { PortfolioCategoryId, PortfolioItemInput } from '../../Portfolio/portfolio.types';
import styles from './AdminPortfolioForm.module.scss';
import { apiUrl } from '../../../services/apiClient';

interface SpecRowState {
  label: string;
  value: string;
  pills: string;
}

interface ReviewRowState {
  question: string;
  answer: string;
}

function todayDisplayDate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}.${m}.${d}`;
}

function AdminPortfolioForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const [session] = useAdminSession();

  const categoryOptions = usePortfolioCategories();
  const [categoryId, setCategoryId] = useState<PortfolioCategoryId>('');
  const [brand, setBrand] = useState('');
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [specTag, setSpecTag] = useState('');
  const [priceText, setPriceText] = useState('');
  const [priceNote, setPriceNote] = useState('');
  const [code, setCode] = useState('');
  const [tone, setTone] = useState(1);
  const [deco, setDeco] = useState('');
  const [date, setDate] = useState(todayDisplayDate());
  const [popularity, setPopularity] = useState(50);
  const [infoTag, setInfoTag] = useState('');
  const [subCopyText, setSubCopyText] = useState('');
  const [specRows, setSpecRows] = useState<SpecRowState[]>([{ label: '', value: '', pills: '' }]);
  const [processTitle, setProcessTitle] = useState('제작 의도 & 디테일');
  const [processLeadText, setProcessLeadText] = useState('');
  const [reviewRows, setReviewRows] = useState<ReviewRowState[]>([{ question: '', answer: '' }]);
  const [isPublished, setIsPublished] = useState(true);

  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [existingThumbnailUrl, setExistingThumbnailUrl] = useState<string | null>(null);
  const [removeThumbnail, setRemoveThumbnail] = useState(false);

  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(isEdit);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || !id || !session) return;
    fetchAdminPortfolioItem(session.token, id)
      .then((item) => {
        setCategoryId(item.categoryId);
        setBrand(item.brand);
        setName(item.name);
        setTag(item.tag);
        setSpecTag(item.specTag);
        setPriceText(item.price === null ? '' : item.price.toLocaleString('ko-KR'));
        setPriceNote(item.priceNote);
        setCode(item.code);
        setTone(item.tone);
        setDeco(item.deco);
        setDate(item.date);
        setPopularity(item.popularity);
        setInfoTag(item.detail.infoTag);
        setSubCopyText(item.detail.subCopy.join('\n'));
        setSpecRows(
          item.detail.specs.length > 0
            ? item.detail.specs.map((s) => ({ label: s.label, value: s.value, pills: (s.pills ?? []).join(', ') }))
            : [{ label: '', value: '', pills: '' }],
        );
        setProcessTitle(item.detail.processTitle);
        setProcessLeadText(item.detail.processLead.join('\n'));
        setReviewRows(
          item.detail.reviews.length > 0
            ? item.detail.reviews.map((r) => ({ question: r.question, answer: r.answer }))
            : [{ question: '', answer: '' }],
        );
        setIsPublished(item.isPublished);
        setExistingThumbnailUrl(item.thumbnailUrl);
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : '주문 상품 정보를 불러오지 못했습니다.'))
      .finally(() => setIsLoading(false));
  }, [isEdit, id, session]);

  // 새로 등록할 때는 카테고리 목록이 도착하면 첫 번째 카테고리를 기본값으로 고른다
  useEffect(() => {
    if (!isEdit && !categoryId && categoryOptions && categoryOptions.length > 0) {
      setCategoryId(categoryOptions[0].id);
    }
  }, [isEdit, categoryId, categoryOptions]);

  const handleThumbnailChange =(file: File | null) => {
    setThumbnailFile(file);
    setRemoveThumbnail(false);
    if (thumbnailPreview) URL.revokeObjectURL(thumbnailPreview);
    setThumbnailPreview(file ? URL.createObjectURL(file) : null);
  };

  const updateSpecRow = (index: number, patch: Partial<SpecRowState>) => {
    setSpecRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const updateReviewRow = (index: number, patch: Partial<ReviewRowState>) => {
    setReviewRows((rows) => rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!session) return;
    setSubmitError(null);

    if (!brand.trim() || !name.trim() || !tag.trim() || !specTag.trim()) {
      setSubmitError('브랜드/상품명/부제/스펙태그는 필수 항목입니다.');
      return;
    }

    if (!categoryId) {
      setSubmitError('카테고리를 선택해주세요.');
      return;
    }

    const priceDigits = priceText.replace(/[^0-9]/g, '');

    const categoryLabel = categoryOptions?.find((c) => c.id === categoryId)?.label ?? '';

    const input: PortfolioItemInput = {
      categoryId,
      brand: brand.trim(),
      name: name.trim(),
      tag: tag.trim(),
      specTag: specTag.trim(),
      price: priceDigits ? Number(priceDigits) : null,
      priceNote: priceNote.trim(),
      code: code.trim() || undefined,
      tone,
      deco: deco.trim() || brand.trim().slice(0, 1).toUpperCase(),
      date: date.trim() || todayDisplayDate(),
      popularity,
      infoTag: infoTag.trim() || `${specTag.trim()} · ${categoryLabel}`,
      subCopy: subCopyText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      specs: specRows
        .filter((row) => row.label.trim() || row.value.trim())
        .map((row) => {
          const pills = row.pills
            .split(',')
            .map((p) => p.trim())
            .filter(Boolean);
          return {
            label: row.label.trim(),
            value: row.value.trim(),
            ...(pills.length > 0 ? { pills } : {}),
          };
        }),
      processTitle: processTitle.trim() || '제작 의도 & 디테일',
      processLead: processLeadText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean),
      reviews: reviewRows
        .filter((row) => row.question.trim() || row.answer.trim())
        .map((row) => ({ question: row.question.trim(), answer: row.answer.trim() })),
      isPublished,
    };

    setIsSubmitting(true);
    try {
      if (isEdit && id) {
        await updatePortfolioItem(session.token, id, input, thumbnailFile, removeThumbnail);
      } else {
        await createPortfolioItem(session.token, input, thumbnailFile);
      }
      navigate('/admin/portfolio');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <AdminLayout title={isEdit ? '주문 상품 수정' : '주문 상품 등록'}>
        <div className="section">
          <div className="card">불러오는 중...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={isEdit ? '주문 상품 수정' : '주문 상품 등록'}>
      <div className="section">
        <div className="sec-head">
          <div className="sec-title">{isEdit ? '주문 상품 수정' : '새 주문 상품 등록'}</div>
          <div className="sec-actions">
            <button type="button" className="btn btn-outline" onClick={() => navigate('/admin/portfolio')}>
              ← 목록으로
            </button>
          </div>
        </div>

        {loadError && (
          <div className="card" style={{ color: '#B91C1C', marginBottom: 16 }}>
            {loadError}
          </div>
        )}

        <form className={styles.form} onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              기본 정보
            </div>
            <div className={styles.grid}>
              <div className={styles.field}>
                <label>카테고리</label>
                <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                  {!categoryId && <option value="">카테고리 선택</option>}
                  {(categoryOptions ?? []).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label>브랜드<span className={styles.required}>*</span></label>
                <input value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="예) GENTLE MONSTER" />
              </div>
              <div className={styles.field}>
                <label>상품명<span className={styles.required}>*</span></label>
                <input value={name} onChange={(e) => setName(e.target.value)} placeholder="예) Octagonal Jewelry Sleeve Box" />
              </div>
              <div className={`${styles.field} ${styles.full}`}>
                <label>부제 (목록에 노출되는 한 줄 설명)<span className={styles.required}>*</span></label>
                <input value={tag} onChange={(e) => setTag(e.target.value)} placeholder="예) 젠틀몬스터 팔각 쥬얼리 싸바리 박스 제작" />
              </div>
              <div className={styles.field}>
                <label>스펙 태그<span className={styles.required}>*</span></label>
                <input value={specTag} onChange={(e) => setSpecTag(e.target.value)} placeholder="예) SLEEVE · CUSTOM" />
              </div>
              <div className={styles.field}>
                <label>기준가 (원, 비우면 '견적 문의'로 표시)</label>
                <input
                  inputMode="numeric"
                  value={priceText}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
                    setPriceText(digits ? Number(digits).toLocaleString('ko-KR') : '');
                  }}
                  placeholder="예) 12,000"
                />
              </div>
              <div className={styles.field}>
                <label>가격 설명</label>
                <input
                  value={priceNote}
                  maxLength={100}
                  onChange={(e) => setPriceNote(e.target.value)}
                  placeholder="예) 100개 기준, VAT 별도"
                />
              </div>
              <div className={styles.field}>
                <label>코드 (미입력 시 자동 생성)</label>
                <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="예) CM-1229" />
              </div>
              <div className={styles.field}>
                <label>날짜</label>
                <input value={date} onChange={(e) => setDate(e.target.value)} placeholder="2026.09.13" />
              </div>
              <div className={styles.field}>
                <label>인기도 (숫자가 높을수록 인기순 상단)</label>
                <input
                  type="number"
                  value={popularity}
                  onChange={(e) => setPopularity(Number(e.target.value) || 0)}
                />
              </div>
              <div className={styles.field}>
                <label>색상 톤 (썸네일 이미지가 없을 때 사용)</label>
                <select value={tone} onChange={(e) => setTone(Number(e.target.value))}>
                  {Array.from({ length: 9 }, (_, i) => i + 1).map((t) => (
                    <option key={t} value={t}>
                      톤 {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label>이니셜 (썸네일 장식 문자)</label>
                <input value={deco} onChange={(e) => setDeco(e.target.value.slice(0, 2))} placeholder="예) G" />
              </div>
              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" checked={isPublished} onChange={(e) => setIsPublished(e.target.checked)} />
                  사용자에게 공개
                </label>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              대표 이미지
            </div>
            <p style={{ fontSize: 12.5, color: 'var(--ink-500)', marginBottom: 10 }}>
              업로드하지 않으면 색상 톤 + 이니셜로 만든 장식 썸네일이 대신 표시됩니다.
            </p>
            {(thumbnailPreview || (existingThumbnailUrl && !removeThumbnail)) && (
              <div className={styles.thumbnailPreviewRow}>
                <img
                  src={thumbnailPreview ?? (existingThumbnailUrl ? apiUrl(existingThumbnailUrl) : undefined)}
                  alt="대표 이미지 미리보기"
                  className={styles.thumbnailPreview}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => {
                    handleThumbnailChange(null);
                    setRemoveThumbnail(true);
                  }}
                >
                  이미지 제거
                </button>
              </div>
            )}
            <input
              type="file"
              accept=".jpg,.jpeg,.png,.gif,.webp"
              onChange={(e) => handleThumbnailChange(e.target.files?.[0] ?? null)}
            />
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              상세 소개
            </div>
            <div className={styles.field} style={{ marginBottom: 14 }}>
              <label>인포 태그</label>
              <input value={infoTag} onChange={(e) => setInfoTag(e.target.value)} placeholder="예) FOIL SLEEVE BOX · CUSTOM" />
            </div>
            <div className={styles.field}>
              <label>부제 설명 (한 줄에 하나씩)</label>
              <textarea value={subCopyText} onChange={(e) => setSubCopyText(e.target.value)} rows={3} />
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              제작 스펙
            </div>
            {specRows.map((row, i) => (
              <div key={i} className={styles.specRow}>
                <input
                  placeholder="항목 (예: 사이즈)"
                  value={row.label}
                  onChange={(e) => updateSpecRow(i, { label: e.target.value })}
                />
                <input
                  placeholder="내용"
                  value={row.value}
                  onChange={(e) => updateSpecRow(i, { value: e.target.value })}
                />
                <input
                  placeholder="태그 (쉼표로 구분, 선택)"
                  value={row.pills}
                  onChange={(e) => updateSpecRow(i, { pills: e.target.value })}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setSpecRows((rows) => rows.filter((_, idx) => idx !== i))}
                  disabled={specRows.length === 1}
                >
                  삭제
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setSpecRows((rows) => [...rows, { label: '', value: '', pills: '' }])}
            >
              + 스펙 행 추가
            </button>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              제작 의도 & 디테일 본문
            </div>
            <div className={styles.field} style={{ marginBottom: 14 }}>
              <label>제목</label>
              <input value={processTitle} onChange={(e) => setProcessTitle(e.target.value)} />
            </div>
            <div className={styles.field}>
              <label>본문 (문단마다 줄바꿈으로 구분)</label>
              <textarea value={processLeadText} onChange={(e) => setProcessLeadText(e.target.value)} rows={5} />
            </div>
          </div>

          <div className="card">
            <div className="card-title" style={{ marginBottom: 12 }}>
              고객 후기
            </div>
            {reviewRows.map((row, i) => (
              <div key={i} className={styles.reviewRow}>
                <input
                  placeholder="후기 제목/질문"
                  value={row.question}
                  onChange={(e) => updateReviewRow(i, { question: e.target.value })}
                />
                <textarea
                  placeholder="답변 내용"
                  value={row.answer}
                  onChange={(e) => updateReviewRow(i, { answer: e.target.value })}
                  rows={2}
                />
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => setReviewRows((rows) => rows.filter((_, idx) => idx !== i))}
                  disabled={reviewRows.length === 1}
                >
                  삭제
                </button>
              </div>
            ))}
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => setReviewRows((rows) => [...rows, { question: '', answer: '' }])}
            >
              + 후기 행 추가
            </button>
          </div>

          {submitError && (
            <div className="card" style={{ color: '#B91C1C' }}>
              {submitError}
            </div>
          )}

          <div className={styles.submitRow}>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? '저장 중...' : isEdit ? '수정 저장' : '등록하기'}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

export default AdminPortfolioForm;
